import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { startGame, kickPlayer, joinLobby } from '../lib/firebase/collections';
import QRCode from 'react-qr-code';
import type { Lobby, GameSettings, Player } from '../types/game';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { getStoredPlayerName, setStoredPlayerName } from '../utils/userUtils';

export const LobbyWaiting = () => {
  const { lobbyId } = useParams<{ lobbyId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [lobby, setLobby] = useState<Lobby | null>(null);
  const [localSettings, setLocalSettings] = useState<GameSettings | null>(null);
  const [playerName, setPlayerName] = useState(() => getStoredPlayerName());
  const [error, setError] = useState('');

  useEffect(() => {
    if (!lobbyId) return;
    const unsubscribe = onSnapshot(doc(db, 'lobbies', lobbyId), (doc) => {
      if (doc.exists()) {
        const lobbyData = doc.data() as Lobby;
        setLobby(lobbyData);
        if (!localSettings && lobbyData.gameSettings) {
          setLocalSettings(lobbyData.gameSettings);
        }
        if (lobbyData.gameState === 'playing') {
          navigate(`/game/${lobbyId}`);
        }
      } else {
        setError('Lobby nicht gefunden.');
        navigate('/');
      }
    }, (err) => {
      console.error(err);
      setError('Fehler beim Laden der Lobby.');
    });
    return () => unsubscribe();
  }, [lobbyId, navigate, localSettings]);
  
  const handleStartGame = async () => {
    if (lobbyId) await startGame(lobbyId);
  };
  
  const handleKickPlayer = async (playerToKickUid: string) => {
    if (lobbyId && user && user.uid === lobby?.hostUid) {
      if (window.confirm("Möchtest du diesen Spieler wirklich entfernen?")) {
        await kickPlayer(lobbyId, user.uid, playerToKickUid);
      }
    }
  };

  const handleJoinLobby = async () => {
    if (!lobbyId || !playerName.trim()) return setError('Bitte gib einen Spielernamen ein');
    if (!user) return setError('Authentifizierung läuft noch, bitte warten...');
    
    setError(''); // Clear previous errors
    setStoredPlayerName(playerName);
    
    try {
      const newPlayer: Player = {
        uid: user.uid,
        name: playerName.trim(),
        score: 0,
        hand: [],
        isCzar: false,
      };
      await joinLobby(lobbyId, newPlayer);
    } catch (e: any) {
      console.error('Fehler beim Beitreten der Lobby:', e);
      
      // Bessere Fehlerbehandlung
      if (e.code === 'permission-denied') {
        setError('Berechtigung verweigert. Bitte lade die Seite neu und versuche es erneut.');
      } else if (e.code === 'not-found') {
        setError('Lobby nicht gefunden.');
      } else if (e.code === 'unavailable') {
        setError('Netzwerkfehler. Bitte prüfe deine Internetverbindung.');
      } else {
        setError(e.message || 'Unbekannter Fehler beim Beitreten. Bitte versuche es erneut.');
      }
    }
  };

  const handleSettingsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!localSettings) return;
    const { name, value, type } = e.target;
    
    let processedValue: string | number | boolean | undefined = value;
    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      processedValue = value === '' ? undefined : parseInt(value, 10);
    }

    setLocalSettings({ ...localSettings, [name]: processedValue });
  };
  
  const handleSaveChanges = async () => {
    if (lobbyId && localSettings) {
      await updateDoc(doc(db, 'lobbies', lobbyId), { gameSettings: localSettings });
      alert("Einstellungen gespeichert!");
    }
  };

  const isPlayerInLobby = useCallback(() => {
    return lobby?.playerUids.includes(user?.uid || '') ?? false;
  }, [lobby, user]);

  if (!lobby || !user || !localSettings) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400"></div>
      </div>
    );
  }

  const lobbyUrl = `${window.location.origin}/lobby/${lobbyId}`;
  const isHost = lobby.hostUid === user.uid;

  // Render Join-UI if user is not in lobby yet
  if (!isPlayerInLobby()) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-gray-900 via-[#10181f] to-[#1a202c] p-3 sm:p-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md rounded-xl sm:rounded-2xl bg-black/30 backdrop-blur-xl p-6 sm:p-8 text-center shadow-2xl border border-white/10">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 break-words">Lobby beitreten</h1>
          <p className="text-gray-400 mb-6 text-sm sm:text-base break-words">Lobby von {lobby.hostName}</p>
          {error && <p className="text-red-400 mb-4 text-sm break-words">{error}</p>}
          <input
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder="Dein Name"
            className="w-full px-4 py-3 rounded-lg bg-white/20 text-primary-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-400 font-semibold text-center text-sm sm:text-base"
          />
          <button
            onClick={handleJoinLobby}
            disabled={!playerName.trim()}
            className="w-full mt-4 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg font-bold text-base sm:text-lg shadow-lg hover:scale-105 transition-transform disabled:opacity-50"
          >
            Beitreten
          </button>
        </motion.div>
      </div>
    );
  }

  // Render Lobby Waiting Room
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-gray-900 via-[#10181f] to-[#1a202c] p-3 sm:p-4 font-sans">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-6xl rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 bg-black/30 backdrop-blur-2xl border border-white/10 flex flex-col gap-4 sm:gap-6 lg:gap-8"
      >
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white font-display tracking-tight break-words">
            Warteraum
          </h1>
          <p className="text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base break-words">Host: {lobby.hostName}</p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
            {/* Players List */}
            <div>
              <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-green-300 font-display">Spieler ({lobby.players.length})</h3>
              <div className="space-y-2 sm:space-y-3 pr-2 sm:pr-4 max-h-60 overflow-y-auto">
                {lobby.players.map((player) => (
                  <motion.div key={player.uid} layout className="flex items-center justify-between bg-white/5 p-2 sm:p-3 rounded-lg border border-transparent hover:border-white/20 transition-all">
                    <span className="font-semibold text-white text-sm sm:text-base truncate pr-2">{player.name} {player.uid === lobby.hostUid && '(Host)'}</span>
                    {isHost && player.uid !== lobby.hostUid && (
                      <button onClick={() => handleKickPlayer(player.uid)} className="text-red-500 hover:text-red-400 text-xs font-bold uppercase tracking-wider flex-shrink-0">
                        Entfernen
                      </button>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Game Settings */}
            <div>
              <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-blue-300 font-display">Einstellungen</h3>
              <div className="space-y-3 sm:space-y-4 text-xs sm:text-sm text-gray-200 bg-white/5 p-4 sm:p-6 rounded-lg max-h-60 sm:max-h-80 overflow-y-auto">
                <div>
                  <label className="font-bold block mb-1">Punkte zum Sieg</label>
                  <input type="number" name="winningScore" value={localSettings.winningScore} onChange={handleSettingsChange} disabled={!isHost} className="input mt-1 text-xs sm:text-sm"/>
                </div>
                <div>
                  <label className="font-bold block mb-1">Maximale Runden (optional)</label>
                  <input
                    type="number"
                    name="maxRounds"
                    value={localSettings.maxRounds === undefined ? '' : localSettings.maxRounds}
                    onChange={e => {
                      handleSettingsChange({
                        ...e,
                        target: {
                          ...e.target,
                          value: e.target.value === '' ? undefined : e.target.value
                        }
                      } as any);
                    }}
                    disabled={!isHost}
                    className="input mt-1 text-xs sm:text-sm"
                    min={1}
                  />
                </div>
                <div>
                  <label className="font-bold block mb-1">Handkartenzahl</label>
                  <input type="number" name="handSize" value={localSettings.handSize} onChange={handleSettingsChange} disabled={!isHost} className="input mt-1 text-xs sm:text-sm" min={1} max={20}/>
                </div>
                <div>
                  <label className="font-bold block mb-1">Rundenzeit (Sekunden)</label>
                  <input type="number" name="roundTimeSeconds" value={localSettings.roundTimeSeconds} onChange={handleSettingsChange} disabled={!isHost || !localSettings.timerEnabled} className="input mt-1 text-xs sm:text-sm" min={10} max={600}/>
                </div>
                <div className="flex items-center justify-between">
                  <label htmlFor="timerEnabled" className="font-bold text-white text-xs sm:text-sm">Timer aktiv</label>
                  <input id="timerEnabled" type="checkbox" name="timerEnabled" checked={localSettings.timerEnabled} onChange={handleSettingsChange} disabled={!isHost} className="h-4 w-4 sm:h-6 sm:w-6 rounded bg-white/20 border-white/30 text-primary-500 focus:ring-primary-400 cursor-pointer"/>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <label htmlFor="allowLateJoin" className="font-bold text-white text-xs sm:text-sm">Late Join erlauben</label>
                  <input id="allowLateJoin" type="checkbox" name="allowLateJoin" checked={localSettings.allowLateJoin} onChange={handleSettingsChange} disabled={!isHost} className="h-4 w-4 sm:h-6 sm:w-6 rounded bg-white/20 border-white/30 text-primary-500 focus:ring-primary-400 cursor-pointer"/>
                </div>
                <p className="text-xs text-gray-400 mb-2 ml-1">Spieler können auch nach Spielstart noch beitreten.</p>
                <div className="flex items-center justify-between mt-2">
                  <label htmlFor="showPlayerNames" className="font-bold text-white text-xs sm:text-sm">Spielernamen anzeigen</label>
                  <input id="showPlayerNames" type="checkbox" name="showPlayerNames" checked={localSettings.showPlayerNames} onChange={handleSettingsChange} disabled={!isHost} className="h-4 w-4 sm:h-6 sm:w-6 rounded bg-white/20 border-white/30 text-primary-500 focus:ring-primary-400 cursor-pointer"/>
                </div>
                <p className="text-xs text-gray-400 mb-2 ml-1">Namen der Spieler werden im Spiel angezeigt.</p>
                <div className="flex items-center justify-between mt-2">
                  <label htmlFor="autoStartNextRound" className="font-bold text-white text-xs sm:text-sm">Automatischer Rundenstart</label>
                  <input id="autoStartNextRound" type="checkbox" name="autoStartNextRound" checked={localSettings.autoStartNextRound} onChange={handleSettingsChange} disabled={!isHost} className="h-4 w-4 sm:h-6 sm:w-6 rounded bg-white/20 border-white/30 text-primary-500 focus:ring-primary-400 cursor-pointer"/>
                </div>
                <p className="text-xs text-gray-400 mb-2 ml-1">Nächste Runde startet automatisch nach kurzer Pause.</p>
                <div className="flex items-center justify-between mt-2">
                  <label htmlFor="allowSkipCards" className="font-bold text-white text-xs sm:text-sm">Karten tauschen erlauben</label>
                  <input id="allowSkipCards" type="checkbox" name="allowSkipCards" checked={localSettings.allowSkipCards} onChange={handleSettingsChange} disabled={!isHost} className="h-4 w-4 sm:h-6 sm:w-6 rounded bg-white/20 border-white/30 text-primary-500 focus:ring-primary-400 cursor-pointer"/>
                </div>
                <p className="text-xs text-gray-400 mb-2 ml-1">Spieler dürfen Karten aus ihrer Hand tauschen.</p>
                {isHost && (
                  <button onClick={handleSaveChanges} className="w-full mt-3 sm:mt-4 py-2 sm:py-3 bg-primary-600 hover:bg-primary-700 rounded-lg font-bold transition-all text-white text-xs sm:text-sm">
                    Einstellungen speichern
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* QR Code & Link */}
          <div className="lg:col-span-1">
            <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-purple-300 font-display">Einladen</h3>
            <div className="bg-white p-3 sm:p-4 rounded-lg">
              <QRCode value={lobbyUrl} size={256} viewBox={`0 0 256 256`} className="w-full h-auto rounded-md" />
            </div>
            <input type="text" value={lobbyUrl} readOnly className="input mt-3 sm:mt-4 text-center text-xs sm:text-sm break-all" onClick={(e) => (e.target as HTMLInputElement).select()} />
          </div>
        </div>

        {isHost && (
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-3 sm:py-4 rounded-xl sm:rounded-2xl bg-gradient-to-r from-primary-600 to-primary-700 text-white font-bold text-base sm:text-lg lg:text-xl shadow-lg shadow-primary-500/30 transition-all mt-4 disabled:from-gray-600 disabled:to-gray-700 disabled:shadow-none disabled:cursor-not-allowed"
            onClick={handleStartGame}
            disabled={lobby.players.length < 2}
          >
            {lobby.players.length < 2 ? 'Mindestens 2 Spieler benötigt' : 'Spiel starten'}
          </motion.button>
        )}
      </motion.div>
    </div>
  );
}; 